/// REACT ///
import { MouseEvent as ReactMouseEvent } from "react";
import { Fragment } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
import useDrawSeams from "hooks/draw_seams";
/// TYPES ///
import { IShopCounter } from "api/types/shop_quote/counter";
import { IShopCutShape } from "api/types/shop_quote/cut_shape";
import { IShopSlab } from "api/types/shop_quote/slab";
import { IShopSeam } from "api/types/shop_quote/seam";
import { IShopPoint } from "api/types/shop_quote/point";
import { MouseData } from "types/mouse_position";
import { Vector2 } from "api/types/sketch";
/// ACTIONS ///
import { createBulkShopCutShapes } from "api/actions/shop_quote";
import { updateLocalShopCutShapes } from "api/actions/shop_quote";
import { updateLocalShopCounter } from "api/actions/shop_quote";
import { updateShopCounter } from "api/actions/shop_quote";
/// MUI ///
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ShopSeamDrawer from "./drawer";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
/// FUNCTIONS ///
import { createShopCutShapes } from "functions/sketch/create/cut_shape";
import { getFirstShopSlabInAreas } from "functions/sketch/get/shop_slab";
import { getCanvas } from "functions/sketch/context";
import { getDistance } from "functions/sketch/math";
/// STYLES ///
import { canvasStyle } from "styles/sketch";
import { sketchAreaStyle } from "styles/sketch";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  mouse_data: MouseData,
  selected_counter: IShopCounter | null,
  setSelectedCounter: (counter: IShopCounter | null) => void
}

const MIN_SEAM_EDGE_MARGIN = 1;
const MIN_SEAM_GAP = 1;

function sanitizePointSeams(point: IShopPoint, segment_distance: number): IShopSeam[] {
  const seams: IShopSeam[] = point.seams ?? [];
  const corner_seams: IShopSeam[] = seams.filter(item => item.is_on_corner);
  const linear_seams: IShopSeam[] = seams
    .filter(item => !item.is_on_corner && typeof item.distance_from_point === "number")
    .sort((a, b) => (a.distance_from_point ?? 0) - (b.distance_from_point ?? 0));

  const max_distance: number = Math.max(0, segment_distance - MIN_SEAM_EDGE_MARGIN);
  const min_distance: number = Math.min(MIN_SEAM_EDGE_MARGIN, max_distance);

  const accepted_linear: IShopSeam[] = [];
  for (let i = 0; i < linear_seams.length; i++) {
    const seam: IShopSeam = linear_seams[i];
    const raw_distance: number = seam.distance_from_point ?? 0;
    const clamped_distance: number = Math.min(max_distance, Math.max(min_distance, raw_distance));
    const last: IShopSeam | undefined = accepted_linear[accepted_linear.length - 1];
    if (!last || Math.abs((last.distance_from_point ?? 0) - clamped_distance) >= MIN_SEAM_GAP) {
      accepted_linear.push({ ...seam, distance_from_point: clamped_distance });
    }
  }

  return [...corner_seams, ...accepted_linear];
}

export default function ShopDefineSeams({
  open,
  setOpen,
  mouse_data,
  selected_counter,
  setSelectedCounter
}: Props) {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const { cut_shapes } = useAppSelector((state) => state.shop_quote);
  const { areas } = useAppSelector((state) => state.shop_quote);
  const [cutting, setCutting] = useState<boolean>(false);
  const [focus_count, setFocusCount] = useState<number>(0);
  const [offset, setOffset] = useState<Vector2>({ X: 0, Y: 0 });

  const active_seam: IShopSeam | null = useDrawSeams(
    mouse_data,
    selected_counter,
    open,
    focus_count,
    offset
  );

  useEffect(() => {
    if (cutting) {
      setSelectedCounter(null);
      setCutting(false);
      setOpen(false);
    }
  }, [cut_shapes, cutting, setOpen, setSelectedCounter]);

  const makeSeams = () => {
    setCutting(true);
    if (selected_counter) {
      const counter: IShopCounter = {
        ...selected_counter,
        points: selected_counter.points.map((point, index, points) => {
          const next_point: IShopPoint | undefined = points[index + 1];
          const segment_distance: number = next_point ? getDistance(point.location, next_point.location) : 0;
          return {
            ...point,
            seams: sanitizePointSeams(point, segment_distance)
          };
        })
      };
      const slab: IShopSlab | null = getFirstShopSlabInAreas(areas, counter?.area_uuid);
      let slab_uuid: string = slab?.uuid ?? areas[0]?.slabs[0]?.uuid;
      const cut_shapes: IShopCutShape[] = createShopCutShapes(
        counter,
        counter.area_uuid,
        slab_uuid,
        counter.points,
        counter.corners,
        [] //offsets
      );
      dispatch(updateLocalShopCutShapes(cut_shapes, counter.uuid));
      dispatch(createBulkShopCutShapes(cut_shapes));
      dispatch(updateLocalShopCounter(counter));
      dispatch(updateShopCounter(counter));
    }
  }

  const handleFocus = () => {
    if (ref && ref.current) {
      const width = ref.current.scrollWidth;
      const height = ref.current.scrollHeight;
      setOffset({ X: ref.current.getBoundingClientRect().x, Y: ref.current.getBoundingClientRect().y });
      const canvas: HTMLCanvasElement | null = getCanvas(document.getElementById("dialog_canvas"));

      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
      setFocusCount(prev => prev + 1);
    }
  }

  const handleEvent = (event: ReactMouseEvent) => {
    if (event.type === "mousedown" && event.button === 0) {
      if (active_seam && selected_counter) {
        setSelectedCounter({
          ...selected_counter, points:
            [...selected_counter.points.map(point => point.uuid === active_seam.point_uuid ?
              {
                ...point, seams: [...point.seams, active_seam].sort(function (a, b) {
                  let x: number | undefined = 0;
                  let y: number | undefined = 0;
                  var i = 0;
                  while (i < point.seams.length) {
                    x = a.distance_from_point;
                    y = b.distance_from_point;
                    i++;
                  }
                  if (x && y) {
                    if (x < y) {
                      return -1;
                    } else if (x > y) {
                      return 1;
                    }
                  }
                  return 0;
                })
              } :
              point
            )]
        });
      }
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xl" onFocus={handleFocus}>
      <DialogTitle>Define Seams</DialogTitle>
      <DialogContent sx={{ marginTop: 2 }}>
        <Grid container>
          <Grid item xs={10.5}>
            <Box
              onContextMenu={(e) => {
                e.preventDefault();
              }}
              sx={[sketchAreaStyle, { height: "75vh" }]}
              onMouseDown={(event: ReactMouseEvent) => handleEvent(event)}
              onMouseUp={(event: ReactMouseEvent) => handleEvent(event)}
              ref={ref}>
              <canvas
                id="dialog_canvas"
                className={JSON.stringify(canvasStyle)}
                style={{ backgroundColor: "#fafafa" }} />
            </Box>
          </Grid>
          <Grid item xs={1.5}>
            <ShopSeamDrawer
              selected_counter={selected_counter}
              setSelectedCounter={setSelectedCounter} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {
          cutting === false ?
            <Fragment>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={makeSeams}>Save</Button>
            </Fragment> :
            <CircularProgress size={22} sx={{ padding: 1, marginRight: 2 }} />
        }
      </DialogActions>
    </Dialog>
  )
}
